// Wizard state machine shared by ExportDesignWizard and its step children.
// Step 3 is a placeholder in this commit — payment wiring lands in the next
// commit and only mutates StepPayment + the post-payment effect in
// ExportDesignWizard.tsx.

export type WizardStep = 1 | 2 | 3;

export interface WizardMetadata {
  name: string;
  notes: string;
}

export interface WizardOptions {
  include3DThumbnail: boolean;
  includeCarbonSummary: boolean;
  includeZonesTable: boolean;
}

export type WizardPaymentStatus = "idle" | "pending" | "succeeded" | "failed";

export interface WizardPayment {
  status: WizardPaymentStatus;
  intentId?: string;
  clientSecret?: string;
  error?: string;
}

export interface WizardState {
  step: WizardStep;
  metadata: WizardMetadata;
  options: WizardOptions;
  payment: WizardPayment;
}

export type WizardAction =
  | { type: "NEXT" }
  | { type: "BACK" }
  | { type: "SET_METADATA"; patch: Partial<WizardMetadata> }
  | { type: "SET_OPTIONS"; patch: Partial<WizardOptions> }
  | { type: "PAYMENT_PENDING"; intentId: string; clientSecret: string }
  | { type: "PAYMENT_SUCCEEDED" }
  | { type: "PAYMENT_FAILED"; error: string };

export function initialWizardState(defaultName: string): WizardState {
  return {
    step: 1,
    metadata: { name: defaultName, notes: "" },
    options: {
      include3DThumbnail: true,
      includeCarbonSummary: true,
      includeZonesTable: true,
    },
    payment: { status: "idle" },
  };
}

export function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case "NEXT":
      return { ...state, step: Math.min(3, state.step + 1) as WizardStep };
    case "BACK":
      return { ...state, step: Math.max(1, state.step - 1) as WizardStep };
    case "SET_METADATA":
      return { ...state, metadata: { ...state.metadata, ...action.patch } };
    case "SET_OPTIONS":
      return { ...state, options: { ...state.options, ...action.patch } };
    case "PAYMENT_PENDING":
      return {
        ...state,
        payment: { status: "pending", intentId: action.intentId, clientSecret: action.clientSecret },
      };
    case "PAYMENT_SUCCEEDED":
      return { ...state, payment: { ...state.payment, status: "succeeded", error: undefined } };
    case "PAYMENT_FAILED":
      return { ...state, payment: { ...state.payment, status: "failed", error: action.error } };
  }
}
